import { useState } from 'react'
import { Button, Input, Label } from '@teach/ui'
import { usePatchContactEmailMutation } from '@/features/auth/authApi'
import { isValidEmail } from '@/lib/email'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/**
 * Инлайн-ввод контактной почты прямо на шаге оплаты — так же, как в боевом
 * плеере (`useInlineEmail`): рублёвая оплата требует почты в профиле, и
 * отдельная модалка ради одного запроса отправила бы автора искать настройки.
 *
 * Своего «успеха» у формы нет намеренно: удачное сохранение инвалидирует тег
 * `Me`, профиль перечитывается, и `useCardPaymentAccess` СВЕРХУ становится
 * `allowed` — рублёвый способ появляется сам, а форма исчезает вместе с
 * причиной. Второй источник признака «можно платить картой» здесь завести
 * нельзя: он разошёлся бы с витриной.
 */
export const ContactEmailForm = () => {
  const [email, setEmail] = useState('')
  const m = useMessages(messages)
  // Ключ, а не готовый текст: смена языка интерфейса перерисует ошибку.
  const [error, setError] = useState<'emailInvalid' | 'emailFailed' | null>(null)
  const [save, { isLoading }] = usePatchContactEmailMutation()

  const submit = async () => {
    const value = email.trim()
    // Мусор до сервера не доезжает: отказ бэка на «aaa» ничем не помог бы
    // автору, а лишний запрос — это ещё и лишнее ожидание.
    if (!isValidEmail(value)) {
      setError('emailInvalid')
      return
    }
    setError(null)
    try {
      await save(value).unwrap()
    } catch {
      // Упавший PATCH обязан сказать о себе: молча оставленное поле выглядит
      // как «нажал и ничего не произошло», и автор жмёт кнопку по кругу.
      setError('emailFailed')
    }
  }

  return (
    <form
      className="space-y-2"
      // Проверку формата делаем сами и своими словами: с браузерной автор
      // увидел бы вместо нашего сообщения нативный пузырёк на языке ОС, а до
      // обработчика клик бы вовсе не дошёл.
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <Label htmlFor="contact-email">{m.emailLabel}</Label>
      <div className="flex gap-2">
        <Input
          id="contact-email"
          type="email"
          value={email}
          placeholder="you@example.com"
          aria-invalid={error !== null}
          onChange={(e) => {
            setEmail(e.target.value)
            if (error) setError(null)
          }}
        />
        <Button type="submit" variant="outline" disabled={isLoading}>
          {m.save}
        </Button>
      </div>
      {error ? <p className="text-sm text-destructive">{m[error]}</p> : null}
    </form>
  )
}
